'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Users, Briefcase, Star, MapPin } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const STATS = [
  {
    id: 'stat-providers',
    icon: Users,
    value: 500,
    suffix: '+',
    label: 'Local Pros',
    iconColor: '#250C31',
    iconBg: 'rgba(153,230,200,0.20)',
    valueColor: '#250C31',
  },
  {
    id: 'stat-rating',
    icon: Star,
    value: 48,
    suffix: '',
    label: 'Avg Rating (out of 5)',
    iconColor: '#F05A28',
    iconBg: 'rgba(240,90,40,0.10)',
    valueColor: '#F05A28',
    displayValue: '4.8',
  },
  {
    id: 'stat-reviews',
    icon: Briefcase,
    value: 1200,
    suffix: '+',
    label: 'Jobs Completed',
    iconColor: '#5BBFA0',
    iconBg: 'rgba(153,230,200,0.15)',
    valueColor: '#5BBFA0',
  },
  {
    id: 'stat-postcodes',
    icon: MapPin,
    value: 100,
    suffix: '%',
    label: 'Verified Profiles',
    iconColor: '#250C31',
    iconBg: 'rgba(37,12,49,0.08)',
    valueColor: '#250C31',
  },
];

function useCountUp(target: number, duration = 1800, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, active]);
  return count;
}

function StatCard({ stat, active }: { stat: typeof STATS[0]; active: boolean }) {
  const count = useCountUp(stat.value, 1800, active);
  const Icon = stat.icon;
  const displayVal = stat.displayValue ?? count.toLocaleString('en-GB') + stat.suffix;

  return (
    <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl border border-gray-100 card-hover" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)' }}>
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
        style={{ backgroundColor: stat.iconBg }}
      >
        <Icon size={22} style={{ color: stat.iconColor }} />
      </div>
      <p className="font-display text-3xl font-extrabold tabular-nums mb-1" style={{ color: stat.valueColor }}>
        {displayVal}
      </p>
      <p className="text-gray-500 text-sm font-medium">{stat.label}</p>
    </div>
  );
}

export default function PlatformStats() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 bg-gray-50 border-y border-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6">
          {STATS.map((stat) => (
            <StatCard key={stat.id} stat={stat} active={active} />
          ))}
        </div>
      </div>
    </section>
  );
}